import { cn } from '@lib/utils/helpers'
import type { InputHTMLAttributes } from 'react'

const SEARCH_SVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-slate-400"
    aria-hidden
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
)

export interface FilterToolbarSearchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  /** Optional label for a11y (announced, not shown) */
  label?: string
  wrapClassName?: string
  inputClassName?: string
  'data-testid'?: string
  /** Optional second testid for the inner input (e.g. invitee-list-search-input) */
  inputDataTestId?: string
  iconClassName?: string
}

/**
 * Search field used in list toolbars (Invitee, Company Assignments, etc.):
 * icon on the left, full-width field, InviteeList-matching focus styles.
 */
export function FilterToolbarSearch({
  value,
  onChange,
  placeholder = 'Search…',
  label = 'Search',
  wrapClassName,
  inputClassName,
  'data-testid': dataTestId = 'filter-toolbar-search',
  inputDataTestId,
  id,
  disabled,
  name,
  autoComplete = 'off',
  iconClassName,
  onKeyDown,
  ...rest
}: FilterToolbarSearchProps) {
  return (
    <div
      data-testid={dataTestId}
      className={cn('relative w-full max-w-lg md:w-1/2', wrapClassName)}
    >
      <div className={cn('absolute left-4 top-1/2 -translate-y-1/2', iconClassName)}>{SEARCH_SVG}</div>
      <input
        id={id}
        name={name}
        type="text"
        data-testid={inputDataTestId}
        className={cn(
          'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-12 pr-4 text-sm shadow-sm transition-all',
          'placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100',
          'disabled:cursor-not-allowed disabled:opacity-60',
          inputClassName,
        )}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoComplete={autoComplete}
        aria-label={label}
        {...rest}
      />
    </div>
  )
}
